import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { httpRequest } from "../../utils/httpRequest";
import { Plus, Trash2, ArrowUp, ArrowDown, Shuffle } from "lucide-react";
import CustomEditor from "../common/Editor";
import ExplanationField from "./ExplanationField";

const makeEmptyForm = () => ({
  stem_fa: "",
  stem_en: "",
  items: [
    { fa: "", en: "", weight: 1, targetIndex: 1, isDistractor: false },
    { fa: "", en: "", weight: 1, targetIndex: 2, isDistractor: false },
  ],
  layout: "top-bottom",
  points: 1,
  status: "published",
  tags: "",
  explanation_fa: "",
  explanation_en: "",
});

const itemToForm = (item) => {
  if (!item) return makeEmptyForm();

  const stem_fa = item?.stem?.fa || "";
  const stem_en = item?.stem?.en || "";

  const body = item.body || {};
  const rawItems = Array.isArray(body.items) ? body.items : [];

  const items =
    rawItems.length > 0
      ? rawItems.map((it, index) => ({
          fa: it?.label?.fa || "",
          en: it?.label?.en || "",
          weight: Number.isFinite(Number(it?.weight)) ? Number(it.weight) : 1,
          targetIndex:
            typeof it?.targetIndex === "number" ? it.targetIndex : index + 1,
          isDistractor: !!it?.isDistractor,
        }))
      : makeEmptyForm().items;

  const layout = body.layout || "top-bottom";
  const points = item?.points ?? 1;
  const status = item?.status || "published";
  const tags = Array.isArray(item?.tags) ? item.tags.join(",") : "";
  const explanation_fa = item?.explanation?.fa || "";
  const explanation_en = item?.explanation?.en || "";

  return {
    stem_fa,
    stem_en,
    items,
    layout,
    points,
    status,
    tags,
    explanation_fa,
    explanation_en,
  };
};

export default function OrderingWeightedModal({
  isOpen,
  onClose,
  token,
  onCreated,
  mode = "create", // "create" | "edit"
  item = null,
}) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language?.includes("fa");
  const isEdit = mode === "edit" && item;

  const [form, setForm] = useState(makeEmptyForm);

  const nonDistractorCount = useMemo(
    () => form.items.filter((x) => !x.isDistractor).length,
    [form.items]
  );

  useEffect(() => {
    if (!isOpen) return;
    if (isEdit) {
      setForm(itemToForm(item));
    } else {
      setForm(makeEmptyForm());
    }
  }, [isOpen, isEdit, item]);

  if (!isOpen) return null;

  const handleEditorChange = (field, content) => {
    setForm((f) => ({ ...f, [field]: content }));
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const addItem = () =>
    setForm((f) => {
      const next = [...f.items];
      const nonDistractors = next.filter((x) => !x.isDistractor).length;
      next.push({
        fa: "",
        en: "",
        weight: 1,
        targetIndex: nonDistractors + 1, // رتبهٔ بعدی
        isDistractor: false,
      });
      return { ...f, items: next };
    });

  const removeItem = (idx) =>
    setForm((f) => {
      const next = f.items.filter((_, i) => i !== idx);
      return { ...f, items: next };
    });

  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= form.items.length) return;
    const next = [...form.items];
    const [it] = next.splice(idx, 1);
    next.splice(j, 0, it);
    setForm((f) => ({ ...f, items: next }));
  };

  const toggleDistractor = (idx) =>
    setForm((f) => {
      const next = [...f.items];
      const cur = next[idx];
      const now = !cur.isDistractor;
      next[idx] = {
        ...cur,
        isDistractor: now,
        targetIndex: now ? null : cur.targetIndex || 1,
      };
      return { ...f, items: next };
    });

  const setWeight = (idx, val) =>
    setForm((f) => {
      const next = [...f.items];
      next[idx].weight = Number(val) || 0;
      return { ...f, items: next };
    });

  const setTarget = (idx, val) =>
    setForm((f) => {
      const next = [...f.items];
      const v = Number(val);
      next[idx].targetIndex = Number.isFinite(v) && v > 0 ? v : null;
      return { ...f, items: next };
    });

  const setFa = (idx, val) =>
    setForm((f) => {
      const next = [...f.items];
      next[idx].fa = val;
      return { ...f, items: next };
    });

  const setEn = (idx, val) =>
    setForm((f) => {
      const next = [...f.items];
      next[idx].en = val;
      return { ...f, items: next };
    });

  // رتبه‌دهی خودکار بر اساس ترتیب فعلی (فقط آیتم‌های غیر حواس‌پرت‌کن)
  const autoAssignTargets = () =>
    setForm((f) => {
      const next = [...f.items];
      let rank = 1;
      next.forEach((it, i) => {
        if (!it.isDistractor) {
          next[i] = { ...it, targetIndex: rank++ };
        } else {
          next[i] = { ...it, targetIndex: null };
        }
      });
      return { ...f, items: next };
    });

  // اعتبارسنجی قبل از ارسال
  const validate = () => {
    if (!form.stem_fa.trim() && !form.stem_en.trim()) {
      toast.error(t("ordering_weighted.toasts.stem_required"));
      return false;
    }
    if (form.items.length < 2) {
      toast.error(t("ordering_weighted.toasts.need_two_items"));
      return false;
    }
    const nonDistractors = form.items.filter((x) => !x.isDistractor);
    if (nonDistractors.length < 2) {
      toast.error(
        t(
          "ordering_weighted.toasts.need_two_rankables",
          "At least two non-distractor items are required."
        )
      );
      return false;
    }
    // رتبه‌ها یکتا و بین 1..K
    const targets = nonDistractors.map((x) => x.targetIndex).filter(Boolean);
    if (targets.length !== nonDistractors.length) {
      toast.error(
        t(
          "ordering_weighted.toasts.targets_missing",
          "Please assign target ranks for all non-distractor items."
        )
      );
      return false;
    }
    const K = nonDistractors.length;
    const validRange = targets.every((n) => n >= 1 && n <= K);
    if (!validRange) {
      toast.error(
        t(
          "ordering_weighted.toasts.targets_range",
          `Ranks must be between 1 and ${K}.`
        )
      );
      return false;
    }
    const uniq = new Set(targets);
    if (uniq.size !== targets.length) {
      toast.error(
        t(
          "ordering_weighted.toasts.targets_unique",
          "Ranks must be unique (no duplicates)."
        )
      );
      return false;
    }
    return true;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = {
      kind: "ordering_weighted",
      explanation: {
        fa: form.explanation_fa || undefined,
        en: form.explanation_en || undefined,
      },
      stem: { fa: form.stem_fa || undefined, en: form.stem_en || undefined },
      body: {
        layout: form.layout,
        items: form.items.map((it) => ({
          label: { fa: it.fa || undefined, en: it.en || undefined },
          weight: Number(it.weight) || 0,
          targetIndex: it.isDistractor ? null : Number(it.targetIndex) || null,
          isDistractor: !!it.isDistractor,
        })),
      },
      scoring: { mode: "order_with_weights", partialCredit: true },
      points: Number(form.points) || 1,
      status: form.status || "published",
      tags: form.tags
        ? form.tags
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
        : [],
    };

    try {
      const isEditMode = isEdit && item?._id;
      const url = isEditMode ? `/item/${item._id}` : "/item";
      const method = isEditMode ? "PUT" : "POST";

      const res = await httpRequest(method, url, payload, {
        "x-access-token": token,
      });
      const doc = res?.data?.data || res?.data;
      if (!doc?._id) throw new Error(res?.message || "FAILED");

      toast.success(
        isEditMode
          ? t(
              "ordering_weighted.toasts.updated",
              "سؤال مرتب‌سازی وزندار با موفقیت ویرایش شد"
            )
          : t(
              "ordering_weighted.toasts.created",
              "سؤال مرتب‌سازی وزندار با موفقیت ساخته شد"
            )
      );
      onCreated?.(doc);
      onClose?.();
    } catch (err) {
      toast.error(err?.message || t("common.unknown_error"));
    }
  };

  return (
    <form onSubmit={submit} className="mb-6 space-y-4">
      {/* Stem */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div dir={isRtl ? "rtl" : "ltr"}>
          <label className="block text-sm font-medium mb-1">
            {t("ordering_weighted.stem_fa")}
          </label>
          <CustomEditor
            value={form.stem_fa}
            onEditorChange={(content) => handleEditorChange("stem_fa", content)}
            placeholder={t("ordering_weighted.stem_fa_ph")}
            directionality={isRtl ? "rtl" : "ltr"}
            height={150}
            toolbar="undo redo | bold italic underline | bullist numlist"
          />
        </div>
        <div dir="ltr">
          <label className="block text-sm font-medium mb-1">
            {t("ordering_weighted.stem_en")}
          </label>
          <CustomEditor
            value={form.stem_en}
            onEditorChange={(content) => handleEditorChange("stem_en", content)}
            placeholder={t("ordering_weighted.stem_en_ph")}
            directionality="ltr"
            height={150}
            toolbar="undo redo | bold italic underline | bullist numlist"
          />
        </div>
      </div>

      {/* Layout */}
      <div>
        <label className="block text-sm font-medium mb-1">
          {t("ordering_weighted.layout_label")}
        </label>
        <div className="flex items-center flex-wrap gap-4 border p-3 rounded-lg bg-gray-50">
          {["top-bottom", "bottom-top", "left-right", "right-left"].map(
            (layout) => (
              <div key={layout} className="flex items-center">
                <input
                  type="radio"
                  id={`layout-${layout}`}
                  name="layout"
                  value={layout}
                  checked={form.layout === layout}
                  onChange={onChange}
                  className="h-4 w-4 text-purple-600 focus:ring-purple-500"
                />
                <label
                  htmlFor={`layout-${layout}`}
                  className="ml-2 text-sm text-gray-700"
                >
                  {t(`ordering_weighted.layout_question_answer_${layout}`)}
                </label>
              </div>
            )
          )}
        </div>
      </div>

      {/* Items */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="block text-sm font-medium">
            {t("ordering_weighted.items")}
          </label>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={autoAssignTargets}
              className="text-gray-700 text-sm inline-flex items-center gap-1 hover:underline"
              title={t("ordering_weighted.auto_assign")}
            >
              <Shuffle size={16} />
              {t(
                "ordering_weighted.auto_assign",
                "Assign ranks by current order"
              )}
            </button>
            <button
              type="button"
              onClick={addItem}
              className="text-blue-600 text-sm inline-flex items-center gap-1"
            >
              <Plus size={16} />
              {t("ordering_weighted.add_item")}
            </button>
          </div>
        </div>

        <div className="space-y-2">
          {form.items.map((it, idx) => (
            <div key={idx} className="border rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold">
                  {t("ordering_weighted.item_n", { n: idx + 1 })}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className="p-1 rounded-full text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                    onClick={() => move(idx, -1)}
                    disabled={idx === 0}
                    title={t("common.move_up")}
                  >
                    <ArrowUp size={16} />
                  </button>
                  <button
                    type="button"
                    className="p-1 rounded-full text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                    onClick={() => move(idx, +1)}
                    disabled={idx === form.items.length - 1}
                    title={t("common.move_down")}
                  >
                    <ArrowDown size={16} />
                  </button>
                  {form.items.length > 2 && (
                    <button
                      type="button"
                      className="p-1 rounded-full text-red-600 hover:bg-red-100"
                      onClick={() => removeItem(idx)}
                      title={t("common.remove")}
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
                {/* FA */}
                <input
                  className="border rounded-lg px-3 py-2"
                  placeholder={t("ordering_weighted.item_fa_ph")}
                  value={it.fa}
                  onChange={(e) => setFa(idx, e.target.value)}
                />
                {/* EN */}
                <input
                  className="border rounded-lg px-3 py-2"
                  placeholder={t("ordering_weighted.item_en_ph")}
                  value={it.en}
                  onChange={(e) => setEn(idx, e.target.value)}
                />

                {/* Weight (score impact) */}
                <div>
                  <label className="block text-xs mb-1">
                    {t("ordering_weighted.weight", "Weight")}
                  </label>
                  <input
                    className="border rounded-lg px-3 py-2 w-full"
                    type="number"
                    min={0}
                    value={it.isDistractor ? 0 : it.weight}
                    onChange={(e) => setWeight(idx, e.target.value)}
                    disabled={it.isDistractor} // distractor → weight=0 و غیرقابل تغییر
                  />
                </div>

                {/* Rank (correct position) — فقط وقتی Distractor نیست */}
                <div>
                  <label className="block text-xs mb-1">
                    {t("ordering_weighted.rank", "Correct position")}
                  </label>
                  <input
                    className="border rounded-lg px-3 py-2 w-full"
                    type="number"
                    min={1}
                    value={it.isDistractor ? "" : it.targetIndex ?? ""}
                    onChange={(e) => setTarget(idx, e.target.value)}
                    disabled={it.isDistractor}
                    placeholder={
                      it.isDistractor
                        ? t("ordering_weighted.no_rank", "—")
                        : "1, 2, 3…"
                    }
                  />
                </div>

                {/* Distractor toggle */}
                <div className="flex items-end">
                  <label className="inline-flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={it.isDistractor}
                      onChange={() => {
                        // وقتی distractor شد: rank=null و weight=0
                        toggleDistractor(idx);
                        // اختیاری: اگر می‌خواهی همان لحظه weight را صفر و rank را خالی کنی
                        // setWeight(idx, 0);
                        // setTarget(idx, null);
                      }}
                    />
                    {t("ordering_weighted.is_distractor", "Distractor")}
                  </label>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-xs text-gray-500 mt-2">
          {t("ordering_weighted.hint_unique_ranks", {
            defaultValue:
              "Give each non-distractor a unique rank (1..{{k}}). Distractors should have no rank.",
            k: nonDistractorCount,
          })}
        </div>
      </div>

      {/* Meta */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="block text-sm font-medium mb-1">
            {t("ordering_weighted.points")}
          </label>
          <input
            type="number"
            min={0}
            className="w-full border rounded-lg px-3 py-2"
            value={form.points}
            onChange={(e) => setForm((f) => ({ ...f, points: e.target.value }))}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">
            {t("ordering_weighted.status")}
          </label>
          <select
            className="w-full border rounded-lg px-3 py-2"
            value={form.status}
            onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
          >
            <option value="published">{t("common.published")}</option>
            <option value="draft">{t("common.draft")}</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">
            {t("ordering_weighted.tags")}
          </label>
          <input
            className="w-full border rounded-lg px-3 py-2"
            placeholder={t("common.tags_csv")}
            value={form.tags}
            onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))}
          />
        </div>
      </div>

      <ExplanationField
        valueFa={form.explanation_fa}
        valueEn={form.explanation_en}
        onChangeFa={(v) => handleEditorChange("explanation_fa", v)}
        onChangeEn={(v) => handleEditorChange("explanation_en", v)}
        isRtl={isRtl}
      />

      <button
        type="submit"
        className="px-4 py-2 bg-blue-600 text-white rounded-lg"
      >
        {isEdit ? t("common.update") : t("common.create")}
      </button>
    </form>
  );
}
